import React from "react";

import { ActionIcon, Group, Paper, SimpleGrid, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconEdit } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";

import StatementSettingsDrawer from "./components/StatementSettingsDrawer";
import { useStatementSettings } from "./hooks/useStatementSettings";

interface StatementSettingsProps {
  readOnly?: boolean;
}

/** Read-only summary of the practice's statement settings, editable through a drawer. */
export default function StatementSettings({ readOnly = false }: StatementSettingsProps) {
  const { settings, fields, isLoading, saveSettings } = useStatementSettings();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  return (
    <Paper p="lg" bg="white" withBorder radius="md" data-testid="statement-settings-card">
      <Group align="center" justify="space-between">
        <Title order={3}>Statement Settings</Title>
        {!readOnly && (
          <ActionIcon
            size="sm"
            color="gray"
            variant="subtle"
            onClick={openDrawer}
            aria-label="Edit statement settings"
            data-testid="statement-settings-edit"
          >
            <IconEdit size={18} />
          </ActionIcon>
        )}
      </Group>

      {isLoading ? (
        <Loading />
      ) : (
        <SimpleGrid mt="md" spacing="xs" verticalSpacing="xs" cols={{ base: 1, sm: 2 }}>
          {fields.map((field) => (
            <React.Fragment key={field.linkId}>
              <TextWrapper c="dark.2" variant="bold">
                {field.label}
              </TextWrapper>
              <TextWrapper fw={500} c="other-black">
                {settings[field.linkId] ?? "—"}
              </TextWrapper>
            </React.Fragment>
          ))}
        </SimpleGrid>
      )}

      <StatementSettingsDrawer
        fields={fields}
        settings={settings}
        onSave={saveSettings}
        opened={drawerOpened}
        onClose={closeDrawer}
      />
    </Paper>
  );
}
